import {
  ButtonColor,
  ButtonSize,
  Combobox,
  Loader,
  Option,
  Table,
  TableRow,
  TextInput,
} from '@code/innovance-react-ui-kit';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ContactInfoIndividualPageValidation, Customer, Document } from 'src/front/api/apiInstance';
import { CardApplicationScreenType, ReferenceDataType } from 'src/front/api/generated';
import usePRM from 'src/front/common/hooks/usePRM';
import { convertStringToDate, formatDate } from 'src/front/common/utils/dateTimeUtils';
import { createDocumentPageDataForValidation } from 'src/front/common/utils/documentPageDataUtils';

import { optionFormat } from 'src/front/common/utils/formUtils';
import {
  convertDocumentIdentityDataToCustomerIdentityData,
  getIdentityList,
} from 'src/front/common/utils/identityDataUtils';
import {
  createIndividualPageDataForValidation,
  FormData,
} from 'src/front/common/utils/individualPageDataUtils';
import AsyncButton from 'src/front/components/AsyncButton';
import Datepicker from 'src/front/components/Datepicker';

import {
  Container,
  FormElement,
  FormRow,
  Separator,
  Subtitle,
  TableWrapper,
  Title,
} from 'src/front/components/FormStyles';
import { IdentityFormData } from 'src/front/components/identity/IdentityFormData';

import {
  useCustomerSearchDispatch,
  useCustomerSearchState,
} from 'src/front/contexts/ContactInfoContext';

const identityTableHeaders = [
  'general_documentType_label',
  'general_documentSeries_label',
  'general_documentNumber_label',
  'general_issueDate_label',
  'general_issuedBy_label',
  'general_expiredDate_label',
  'cardEntry_documentEntry_unitCode_label',
  'changeFundClientInfo_activityFlag_text',
];

const defaultValues = {
  customerName: '',
  customerSurname: '',
  customerSecondName: '',
  customerGender: null,
  birthDate: null,
  birthPlaceCountry: null,
  citizenship: null,
  birthPlaceRegion: null,
  residenceCountry: null,
  birthPlaceCity: '',
  hasPreviousSurname: null,
  previousSurname: '',
};
interface Props {
  customerId?: string;
  customerNumber?: string;
  goToNextStep: () => void;
  isEdit?: boolean;
  isEditCustomerName?: string;
}
interface Identity extends IdentityFormData {
  isNew?: boolean;
  givenCountry?: string;
}
interface ContactPageClientData {
  individualData: FormData;
  identityList: Identity[];
}
const defaultPageData = {
  individualData: { ...defaultValues },
  identityList: [],
};
const ClientInformation: FC<Props> = ({ goToNextStep, isEdit }) => {
  const { t } = useTranslation();

  const customerSearchDispatch = useCustomerSearchDispatch();
  const { customerId, customerNumber } = useCustomerSearchState();
  const [identityTypes, setIdentityTypes] = useState<Option[]>([]);
  const [pageData, setPageData] = useState<ContactPageClientData>(defaultPageData);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [editCustomerInfo, setEditCustomerInfo] = useState<{
    customerId?: string;
    customerNumber?: string;
  }>();

  const countries = usePRM(ReferenceDataType.COUNTRIES);
  const genders = usePRM(ReferenceDataType.GENDER);
  const regions = usePRM(ReferenceDataType.REGION);
  const yesNoComboItems = usePRM(ReferenceDataType.YESNOTYPE);

  const getContactInformationData = useCallback(async (): Promise<void> => {
    const { data } = await Customer.getCustomerDetailForCardApplication({
      customerId: customerId || '',
    });
    const location = countries.find((country) => country.value === data?.residence) || null;
    const individualData: FormData = {
      birthDate: convertStringToDate(data.birthDate),
      birthPlaceCity: data.birthPlaceText || '',
      birthPlaceCountry: location,
      birthPlaceRegion: regions.find((region) => region.value === data?.birthPlaceRegion) || null,
      citizenship: location,
      customerGender: genders.find((gender) => gender.value === data?.gender) || null,
      customerName: data.customerName || '',
      customerSecondName: data.customerSecondName || '',
      customerSurname: data.customerSurname || '',
      residenceCountry: location,
      hasPreviousSurname: null,
      previousSurname: '',
    };
    const { data: response } = await Customer.getCustomerIdentities({
      customerId,
      cardApplicationScreenType: CardApplicationScreenType.CLIENTINFOCHANGE,
    });
    const identityList = getIdentityList(identityTypes, response.customerIdentityList);

    setPageData({ individualData, identityList });
  }, [countries, customerId, genders, identityTypes, regions]);

  const loadContactInformationData = useCallback(async (): Promise<void> => {
    const { data } = await ContactInfoIndividualPageValidation.getContactInfoIndividualPageData();
    let individualData: FormData = { ...defaultValues };
    // Checks if data is in session
    if (data.result === 'OK' && data.pageData) {
      const { individualData: clientData, documentIdentityDataList } = data.pageData;
      if (clientData) {
        individualData = {
          birthDate: convertStringToDate(clientData.birthDate),
          birthPlaceCity: clientData.birthPlaceCity || '',
          birthPlaceCountry: clientData.birthPlaceCountry
            ? optionFormat(clientData.birthPlaceCountry)
            : null,
          birthPlaceRegion: optionFormat(clientData.birthPlaceRegion),
          citizenship: optionFormat(clientData.citizenship),
          customerGender: clientData.customerGender
            ? optionFormat(clientData.customerGender)
            : null,
          customerName: clientData.customerName || '',
          customerSecondName: clientData.customerSecondName || '',
          customerSurname: clientData.customerSurname || '',
          hasPreviousSurname: clientData.hasPreviousSurname
            ? optionFormat(clientData.hasPreviousSurname)
            : null,
          previousSurname: clientData.previousSurname || '',
          residenceCountry: optionFormat(clientData.residenceCountry),
        };
        const identityList = getIdentityList(
          identityTypes,
          convertDocumentIdentityDataToCustomerIdentityData(documentIdentityDataList)
        );
        setPageData({ individualData, identityList });

        isEdit &&
          setEditCustomerInfo({
            customerId: clientData.customerId,
            customerNumber: clientData.customerNumber,
          });
      }
    } else {
      // Data is not in the session
      await getContactInformationData();
    }
    setIsLoading(false);
  }, [getContactInformationData, identityTypes, isEdit]);

  useEffect(() => {
    if (genders.length && countries.length && regions.length) {
      loadContactInformationData();
    }
  }, [countries.length, customerId, genders.length, loadContactInformationData, regions.length]);
  useEffect(() => {
    (async () => {
      const { data } = await Document.getDocumentTypeByScreenType({});

      if (data.result === 'OK') {
        setIdentityTypes(data.documentTypeList.map(optionFormat));
      }
    })();
  }, []);

  const handleContinue = async (): Promise<void> => {
    const { individualData: form, identityList } = pageData;
    const res = await ContactInfoIndividualPageValidation.validateContactInfoIndividualPageData({
      individualData: {
        customerId: customerId || editCustomerInfo?.customerId,
        customerNumber: customerNumber || editCustomerInfo?.customerNumber,
        ...createIndividualPageDataForValidation(form, yesNoComboItems),
      },
      documentIdentityDataList: createDocumentPageDataForValidation(identityList),
    });
    customerSearchDispatch({
      customerId: customerId || editCustomerInfo?.customerId,
    });
    if (res.data.result === 'OK') {
      goToNextStep();
    }
  };
  const identityTableData: TableRow[] = pageData.identityList.map(
    (identity: Identity): TableRow => ({
      id: identity.id || '',
      columns: [
        identity.identityType?.label || '',
        identity.identityDocumentSerialCode,
        identity.identityDocumentSerialNumber,
        formatDate(identity.issueDate),
        identity.issuedBy,
        formatDate(identity.expireDate),
        identity.unitCode,
        identity.isActive ? '1' : '0',
      ],
      deletable: identity.isNew,
    })
  );
  const { individualData: customerData } = pageData;
  return (
    <Container>
      <Title>
        <Trans i18nKey="changeContactInfo_title" />
      </Title>
      <Separator />

      {isLoading ? <Loader /> : null}
      <Subtitle>
        <Trans i18nKey="cardEntry_customerInfo_title" />
      </Subtitle>

      <FormRow>
        <FormElement>
          <TextInput
            label={t('changeFundClientInfo_surname_label')}
            maxLength={60}
            name="customerSurname"
            value={customerData.customerSurname}
            disabled
            required
          />
        </FormElement>
        <FormElement>
          <TextInput
            label={t('changeFundClientInfo_name_label')}
            maxLength={40}
            name="customerName"
            value={customerData.customerName}
            disabled
            required
          />
        </FormElement>
      </FormRow>
      <FormRow>
        <FormElement>
          <TextInput
            label={t('changeFundClientInfo_middlename_label')}
            maxLength={30}
            name="customerSecondName"
            value={customerData.customerSecondName}
            disabled
          />
        </FormElement>
        <FormElement>
          <Combobox
            items={genders}
            label={t('changeFundClientInfo_gender_label')}
            name="customerGender"
            value={customerData.customerGender}
            disabled
            required
          />
        </FormElement>
      </FormRow>
      <Separator />
      <Subtitle>
        <Trans i18nKey="changeFundClientInfo_birthPlace_label" />{' '}
      </Subtitle>
      <FormRow>
        <FormElement>
          <Datepicker
            inputLabel={`${t('changeFundClientInfo_birthdate_label')} *`}
            value={customerData.birthDate as Date}
            disabled
          />
        </FormElement>
        <FormElement>
          <Combobox
            items={countries}
            label={t('changeFundClientInfo_birthPlaceCountry_label')}
            name="birthPlaceCountry"
            searchPlaceholder={t('cardEntry_customerInfo_searchPlaceOfBirth_text')}
            value={customerData.birthPlaceCountry}
            disabled
            required
            searchable
          />
        </FormElement>
      </FormRow>
      <FormRow>
        <FormElement>
          <Combobox
            items={countries}
            label={t('changeFundClientInfo_citizenship_label')}
            name="citizenship"
            searchPlaceholder={t('cardEntry_customerInfo_searchCitizenship_text')}
            value={customerData.citizenship}
            disabled
            required
            searchable
          />
        </FormElement>
        <FormElement>
          {customerData.birthPlaceCountry?.value === 'RU' ? (
            <Combobox
              items={regions}
              label={t('changeFundClientInfo_birthPlaceRegion_label')}
              name="birthPlaceRegion"
              searchPlaceholder={t('cardEntry_customerInfo_searchPlaceOfBirthRegion_text')}
              value={customerData.birthPlaceRegion}
              disabled
              required
              searchable
            />
          ) : null}
        </FormElement>
      </FormRow>
      <FormRow>
        <FormElement>
          <Combobox
            items={countries}
            label={t('changeFundClientInfo_residenceCountry_label')}
            name="residenceCountry"
            searchPlaceholder={t('cardEntry_customerInfo_searchPlaceOfBirth_text')}
            value={customerData.residenceCountry}
            disabled
            required
            searchable
          />
        </FormElement>
        <FormElement>
          <TextInput
            label={t('changeFundClientInfo_birthPlace_label')}
            maxLength={100}
            name="birthPlaceCity"
            value={customerData.birthPlaceCity}
            disabled
            required
          />
        </FormElement>
      </FormRow>
      <Separator />
      <FormRow>
        <FormElement>
          <TableWrapper>
            <Table
              addActionText={t('general_add_button')}
              emptyTableText={t('cardEntry_documentEntry_noDocumentInfoFound_text')}
              headers={identityTableHeaders.map((head) => t(head))}
              items={identityTableData}
              loading={isLoading}
              pageNumber={1}
              widths={['22', '9', '14', '14', '11', '11', '12', '7']}
              disabled
            />
          </TableWrapper>
        </FormElement>
      </FormRow>
      <Separator />
      <FormRow>
        <AsyncButton
          color={ButtonColor.Primary}
          onClick={handleContinue}
          size={ButtonSize.Medium}
          text={t('general_continue_button')}
          type="button"
        />
      </FormRow>
    </Container>
  );
};

export default ClientInformation;
